import { useState } from 'react';
import { useGameStore, useJwt, useAuthHeaders } from '@campus/core';
import { getTenantSlug } from '../../lib/tenant.js';
import { AvatarEditor } from '../avatar/AvatarEditor.js';
import { AvatarSaveError, draftFrom, saveAvatar } from '../avatar/pcAppearance.js';
import { API_BASE } from '../../lib/apiBase.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { Button, Input } from '../ds/index.js';

/**
 * AvatarPage — Écran premier login / onboarding.
 * Affiché si authState === 'authenticated' (après login, avant lobby).
 * L'utilisateur crée son avatar puis passe par le lobby (LobbyPage) avant
 * d'entrer dans le campus.
 */
export function AvatarPage() {
  const storedName   = useGameStore((s) => s.displayName);
  const storedAppearance = useGameStore((s) => s.appearance);
  const jwt          = useJwt();
  const authHeaders  = useAuthHeaders(getTenantSlug());
  const setProfile   = useGameStore((s) => s.setProfile);
  const setAppearance = useGameStore((s) => s.setAppearance);
  const setAuthState = useGameStore((s) => s.setAuthState);
  const isMobile = useIsMobile();

  const [name, setName] = useState(storedName ?? '');
  const [draft, setDraft] = useState(() => draftFrom(storedAppearance));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();

  async function handleSave() {
    if (!trimmed || !jwt) return;
    setSaving(true);
    setError(null);
    try {
      // La planche doit exister avant d'entrer : on attend l'API (contrairement au nom)
      const appearance = await saveAvatar(draft, authHeaders);
      fetch(`${API_BASE}/auth/me`, {
        method: 'PATCH', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: trimmed }),
      }).catch(() => {});
      setProfile(trimmed, 0);
      setAppearance(appearance);
      setAuthState('lobby');
    } catch (err) {
      setError(err instanceof AvatarSaveError ? err.message : 'Impossible d\'enregistrer ton avatar. Réessaie.');
      setSaving(false);
    }
  }

  return (
    // html/body ne défilent pas (jeu plein écran). Sur ordinateur, la fenêtre ne dépasse
    // jamais l'écran : la liste des options prend la hauteur qui reste, le bouton Continuer
    // reste visible. Écran trop petit même pour ça : la fenêtre défile. Sur mobile, la page
    // défile. margin: auto centre la fenêtre sans couper son haut.
    <div className="pl-page" style={{
      height: '100dvh', overflowY: 'auto', boxSizing: 'border-box', display: 'flex',
      padding: isMobile ? 0 : 24,
    }}>
      <main style={{
        width: '100%', maxWidth: 720, boxSizing: 'border-box', margin: 'auto',
        minHeight: isMobile ? '100dvh' : undefined,
        ...(isMobile ? {} : { maxHeight: '100%', overflowY: 'auto' as const }),
        display: 'flex', flexDirection: 'column', gap: 24,
        padding: isMobile ? 'calc(20px + env(safe-area-inset-top, 0px)) 16px calc(20px + env(safe-area-inset-bottom, 0px))' : '32px',
        background: 'var(--color-bg-surface)',
        border: isMobile ? 0 : '1px solid var(--color-border-default)',
        borderRadius: isMobile ? 0 : 'var(--radius-xl)',
        boxShadow: isMobile ? 'none' : 'var(--shadow-lg)',
      }}>
        <header style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h1 style={{ margin: 0, font: '800 24px/1.2 var(--font-display)', color: 'var(--color-text-primary)' }}>Crée ton avatar</h1>
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            Les autres membres de l'espace te verront comme ça. Tu pourras le changer à tout moment.
          </p>
        </header>

        <AvatarEditor
          value={draft}
          onChange={setDraft}
          headers={authHeaders}
          compact={isMobile}
          aside={
            <Input
              label="Nom affiché"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ton prénom ou un pseudo"
              maxLength={30}
            />
          }
        />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: isMobile ? 'auto' : 0, flexWrap: 'wrap' }}>
          {error && <span role="alert" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-danger)', flex: 1 }}>{error}</span>}
          <Button variant="primary" onClick={() => { void handleSave(); }} disabled={!trimmed || saving} style={isMobile ? { flex: 1, height: 44 } : undefined}>
            {saving ? 'Enregistrement…' : 'Continuer'}
          </Button>
        </div>
      </main>
    </div>
  );
}
